import React, { useEffect, useState, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useT, Namespace } from "@racoon/i18n";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useToast,
} from "@racoon/ui";
import { ConnectProto } from "@racoon/api";
import {
  ALL_LOCATION_TYPES,
  useCreateLocationMutation,
  useUpdateLocationMutation,
} from "~/shared/api/locations/locations.grpc";
import { MapWithGeofence } from "~/features/map-view/ui/map-view";
import { InfoType } from "~/features/info-panel/model/useInfoPanel";
import { MAP_DEFAULT_CENTER, getEnumName, parseTwoGisCoords } from "@racoon/lib";

interface DepotFormData {
  name: string;
  type: ConnectProto.LocationType;
  address: string;
  lat: number;
  lon: number;
  gis_link?: string;
  service_time?: number;
}

interface DepotDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  depot?: ConnectProto.Location | null;
  selectedCompanyId?: number;
}

export const DepotDialog: React.FC<DepotDialogProps> = ({
  open,
  onOpenChange,
  depot,
  selectedCompanyId,
}) => {
  const t = useT();
  const { toast } = useToast();
  const isEdit = !!depot;

  const [createDepot, { isLoading: isCreating }] = useCreateLocationMutation();
  const [updateDepot, { isLoading: isUpdating }] = useUpdateLocationMutation();

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<DepotFormData>({
    defaultValues: {
      name: "",
      type: ConnectProto.LocationType.DEPOT,
      address: "",
      lat: 0,
      lon: 0,
      gis_link: "",
      service_time: 0,
    },
  });

  const [mapCenter, setMapCenter] = useState<[number, number]>([
    MAP_DEFAULT_CENTER.lat,
    MAP_DEFAULT_CENTER.lng,
  ]);
  const [mapZoom, setMapZoom] = useState<number>(15);

  const gisLink = watch("gis_link");
  const lat = watch("lat");
  const lon = watch("lon");
  const name = watch("name");
  const locationType = watch("type");

  useEffect(() => {
    if (depot) {
      reset({
        name: depot.name ?? "",
        type: depot.type ?? ConnectProto.LocationType.DEPOT,
        address: depot.address ?? "",
        lat: depot.lat ?? 0,
        lon: depot.lon ?? 0,
        gis_link: depot.gisLink ?? "",
        service_time: depot.serviceTime != null ? Number(depot.serviceTime) : 0,
      });
      if (depot.lat && depot.lon) {
        setMapCenter([depot.lat, depot.lon]);
      }
    } else {
      reset({
        name: "",
        type: ConnectProto.LocationType.DEPOT,
        address: "",
        lat: 0,
        lon: 0,
        gis_link: "",
        service_time: 0,
      });
      setMapCenter([MAP_DEFAULT_CENTER.lat, MAP_DEFAULT_CENTER.lng]);
    }
  }, [depot, reset]);

  // 1. Parse link -> Update Form
  useEffect(() => {
    if (gisLink) {
      const coords = parseTwoGisCoords(gisLink);
      if (coords) {
        setValue("lat", coords.lat);
        setValue("lon", coords.lon);
      }
    }
  }, [gisLink, setValue]);

  // 2. Watch Lat/Lon -> Update Map Center
  useEffect(() => {
    const latNum = Number(lat);
    const lonNum = Number(lon);

    if (!isNaN(latNum) && !isNaN(lonNum) && (latNum !== 0 || lonNum !== 0)) {
      if (latNum !== mapCenter[0] || lonNum !== mapCenter[1]) {
        setMapCenter([latNum, lonNum]);
        setMapZoom(15);
      }
    }
  }, [lat, lon, mapCenter]);

  const locationObject = useMemo(() => {
    if (lat != null && lon != null && (Number(lat) !== 0 || Number(lon) !== 0)) {
      return {
        // int64 ids can exceed Number.MAX_SAFE_INTEGER — keep them as strings.
        id: depot ? String(depot.id) : 1,
        name: name || t(Namespace.Settings, "depot.defaultName"),
        type: "depot" as InfoType,
        locationType,
        lat: Number(lat),
        lon: Number(lon),
      };
    }
    return undefined;
  }, [lat, lon, name, depot?.id, locationType, t]);

  const onSubmit = async (data: DepotFormData) => {
    if (!selectedCompanyId) {
      toast({
        title: t(Namespace.Settings, "depot.dialog.error"),
        description: t(Namespace.Validation, "companyIdRequired"),
        variant: "destructive",
      });
      return;
    }

    try {
      if (isEdit && depot) {
        await updateDepot({
          id: BigInt(depot.id),
          ownerOrganizationId: BigInt(selectedCompanyId!),
          name: data.name,
          address: data.address,
          lat: data.lat,
          lon: data.lon,
          gisLink: data.gis_link,
          serviceTime: data.service_time ? BigInt(data.service_time) : undefined,
          type: data.type,
        }).unwrap();
        toast({ title: t(Namespace.Settings, "depot.dialog.updateSuccess") });
      } else {
        await createDepot({
          name: data.name,
          ownerOrganizationId: BigInt(selectedCompanyId!),
          lat: data.lat,
          lon: data.lon,
          address: data.address,
          gisLink: data.gis_link,
          serviceTime: data.service_time ? BigInt(data.service_time) : undefined,
          type: data.type,
        }).unwrap();
        toast({ title: t(Namespace.Settings, "depot.dialog.createSuccess") });
      }
      onOpenChange(false);
      reset();
    } catch (error) {
      toast({
        title: t(Namespace.Settings, "depot.dialog.error"),
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive",
      });
    }
  };

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent closeButtonTop="top-4" size="2xl">
        <ModalHeader>
          <ModalTitle>
            {isEdit
              ? t(Namespace.Settings, "depot.dialog.editTitle")
              : t(Namespace.Settings, "depot.dialog.createTitle")}
          </ModalTitle>
        </ModalHeader>

        <ModalBody>
          <div className="flex flex-col md:flex-row gap-6">
            {/* Form */}
            <div className="w-full md:w-1/2">
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" id="depot-form">
                <div className="space-y-2">
                  <Label htmlFor="type">{t(Namespace.Settings, "depot.dialog.type")} *</Label>
                  <Select
                    value={String(locationType)}
                    onValueChange={(value) =>
                      setValue("type", Number(value) as ConnectProto.LocationType)
                    }
                  >
                    <SelectTrigger id="type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ALL_LOCATION_TYPES.map((type) => (
                        <SelectItem key={type} value={String(type)}>
                          {t(
                            Namespace.Enum,
                            getEnumName(ConnectProto.LocationTypeSchema, type) ??
                              "LOCATION_TYPE_UNDEFINED",
                          )}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="name">{t(Namespace.Settings, "depot.dialog.name")} *</Label>
                  <Input
                    id="name"
                    {...register("name", { required: true })}
                    placeholder={t(Namespace.Settings, "depot.dialog.namePlaceholder")}
                  />
                  {errors.name && (
                    <p className="text-sm text-red-600">{t(Namespace.Validation, "required")}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="address">{t(Namespace.Settings, "depot.dialog.address")} *</Label>
                  <Input
                    id="address"
                    {...register("address", { required: true })}
                    placeholder={t(Namespace.Settings, "depot.dialog.addressPlaceholder")}
                  />
                  {errors.address && (
                    <p className="text-sm text-red-600">{t(Namespace.Validation, "required")}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="gis_link">{t(Namespace.Settings, "depot.dialog.gisLink")}</Label>
                  <Input
                    id="gis_link"
                    {...register("gis_link")}
                    placeholder="https://2gis.ru/..."
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="lat">{t(Namespace.Settings, "depot.dialog.latitude")} *</Label>
                    <Input
                      id="lat"
                      type="number"
                      step="any"
                      {...register("lat", { required: true, valueAsNumber: true })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lon">{t(Namespace.Settings, "depot.dialog.longitude")} *</Label>
                    <Input
                      id="lon"
                      type="number"
                      step="any"
                      {...register("lon", { required: true, valueAsNumber: true })}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="service_time">
                    {t(Namespace.Settings, "depot.dialog.serviceTime")}
                  </Label>
                  <Input
                    id="service_time"
                    type="number"
                    {...register("service_time", { valueAsNumber: true })}
                  />
                </div>
              </form>
            </div>

            {/* Map */}
            <div className="w-full md:w-1/2">
              <div className="rounded-lg border bg-muted/20 h-[400px] overflow-hidden flex items-center justify-center relative">
                {locationObject ? (
                  <MapWithGeofence
                    key={`${mapCenter[0]}-${mapCenter[1]}`}
                    center={mapCenter}
                    zoom={mapZoom}
                    className="w-full h-full"
                    onMarkerClick={() => {}}
                    objects={[locationObject]}
                  />
                ) : (
                  <p className="px-6 text-center text-muted-foreground">
                    {t(Namespace.PickupLocation, "forShowMapUseLink", { link: "2GIS" }) +
                      " или введите координаты"}
                  </p>
                )}
              </div>
            </div>
          </div>
        </ModalBody>

        <ModalFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isCreating || isUpdating}
          >
            {t(Namespace.Buttons, "cancel")}
          </Button>
          <Button type="submit" form="depot-form" disabled={isCreating || isUpdating}>
            {isCreating || isUpdating
              ? t(Namespace.Settings, "buttons.saving")
              : t(Namespace.Settings, "buttons.save")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
